/**
 * Utilitaire pour réessayer une opération avec backoff exponentiel
 * Utile pour gérer les erreurs réseau temporaires (QUIC, timeout, etc.)
 */

/**
 * Détecte si une erreur est une erreur réseau récupérable
 */
export const isRetryableError = (error) => {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code?.toLowerCase() || '';
  
  // Erreurs réseau récupérables
  const retryablePatterns = [
    'quic',
    'network',
    'timeout',
    'unavailable',
    'deadline',
    'connection',
    'failed to fetch',
    'networkerror',
    'too many',
    'retransmission'
  ];
  
  // Codes d'erreur Firebase récupérables (permission-denied n'est pas un souci réseau)
  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'aborted'
  ];
  
  return (
    retryablePatterns.some(pattern => 
      errorMessage.includes(pattern) || errorCode.includes(pattern)
    ) ||
    retryableCodes.some(code => errorCode === code)
  );
};

/**
 * Attendre un délai avec backoff exponentiel
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Réessayer une opération avec backoff exponentiel
 * 
 * @param {Function} operation - Fonction async à exécuter
 * @param {Object} options - Options de retry
 * @param {number} options.maxRetries - Nombre maximum de tentatives (défaut: 3)
 * @param {number} options.initialDelay - Délai initial en ms (défaut: 1000)
 * @param {number} options.maxDelay - Délai maximum en ms (défaut: 10000)
 * @param {Function} options.shouldRetry - Fonction pour déterminer si on doit réessayer (défaut: isRetryableError)
 * @returns {Promise} Résultat de l'opération
 */
export const retryWithBackoff = async (
  operation,
  options = {}
) => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = isRetryableError
  } = options;

  let lastError;
  let delayMs = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Si c'est la dernière tentative ou si l'erreur n'est pas récupérable, lancer l'erreur
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }
      
      // Attendre avant de réessayer (backoff exponentiel)
      await delay(Math.min(delayMs, maxDelay));
      delayMs *= 2; // Double le délai pour la prochaine tentative
    }
  }
  
  throw lastError;
};

/**
 * Wrapper pour les opérations Firestore avec retry automatique
 */
export const firestoreWithRetry = async (operation, options = {}) => {
  return retryWithBackoff(operation, {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 8000,
    ...options
  });
};



